import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Avatar,
  IconButton,
  Tooltip,
  Link as MuiLink,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Public,
  VisibilityOff,
  AccessTime,
  Cake,
  ThumbUp,
  ThumbUpOutlined,
  Lock,
  OpenInNew,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { UserMemory } from '../../types/Memory';
import { UserProfile } from '../../types/UserProfile';
import { toggleMemoryLike } from '../../services/api';
import { formatRelativeTime, calculateAgeAtPlay, calculateYearsAgo, calculateSchoolGrade } from '../../utils/dateUtils';
import { formatReviewText } from '../../utils/formatText';

interface MemoryItemProps {
  memory: UserMemory;
  isListView?: boolean;
  onUpdate?: () => void;
  showUserInfo?: boolean;
  userProfile?: UserProfile | null;
  isPublic?: boolean;
}

const MemoryItem: React.FC<MemoryItemProps> = ({ 
  memory, 
  isListView = false,
  onUpdate,
  showUserInfo = false,
  userProfile = null,
  isPublic = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLiked, setIsLiked] = useState(memory.user_liked || false);
  const [likeCount, setLikeCount] = useState(memory.like_count);
  const [userLikeCount, setUserLikeCount] = useState(memory.user_like_count || 0);

  const maxLength = isListView ? (isMobile ? 120 : 180) : (isMobile ? 180 : 320);
  const maxLines = isListView ? (isMobile ? 4 : 5) : (isMobile ? 8 : 8);

  // memoryの変更を監視してstateを更新
  useEffect(() => {
    setIsLiked(memory.user_liked || false);
    setLikeCount(memory.like_count);
    setUserLikeCount(memory.user_like_count || 0);
  }, [memory.user_liked, memory.like_count, memory.user_like_count]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const truncateText = (text: string) => {
    const lines = text.split('\n');
    if (lines.length > maxLines || text.length > maxLength) {
      const truncatedLines = lines.slice(0, maxLines);
      let truncatedText = truncatedLines.join('\n');
      if (truncatedText.length > maxLength) {
        truncatedText = truncatedText.slice(0, maxLength);
      }
      return `${truncatedText}...`;
    }
    return text;
  };

  const formatText = (text: string) => {
    return formatReviewText(text, isMobile);
  };

  const formatPlayDate = (memory: UserMemory) => {
    // ステータスの日付を優先して使用
    let dateToUse = null;
    if (memory.status_play_end_date) {
      dateToUse = memory.status_play_end_date;
    } else if (memory.status_play_start_date) {
      dateToUse = memory.status_play_start_date;
    } else if (memory.play_date) {
      // フォールバックとしてメモリーのplay_dateを使用
      dateToUse = memory.play_date;
    }

    if (!dateToUse) return null;
    
    const playDate = new Date(dateToUse);
    
    // 年数と年齢の情報を計算
    const yearsAgo = calculateYearsAgo(dateToUse);
    const now = new Date();
    const monthsAgo = (now.getFullYear() - playDate.getFullYear()) * 12 + (now.getMonth() - playDate.getMonth());
    
    let ageAtPlay = null;
    
    // 年齢情報の表示制御
    // - 自分のプロフィールページ（isPublic=false）: age_privacyに関係なく常に詳細表示
    // - 公開プロフィール（isPublic=true）: age_privacyに従って制御
    if (userProfile?.birth_date) {
      const exactAge = calculateAgeAtPlay(userProfile.birth_date, dateToUse);
      
      if (exactAge !== null && (!isPublic || userProfile.age_privacy > 0)) {
        if (!isPublic || userProfile.age_privacy === 2) {
          // 詳細: 学年がある場合は学年、それ以外は実際の年齢を表示
          const schoolGrade = calculateSchoolGrade(userProfile.birth_date, dateToUse);
          if (schoolGrade && (schoolGrade.includes('小学') || schoolGrade.includes('中学'))) {
            ageAtPlay = schoolGrade;
          } else {
            ageAtPlay = exactAge; // 実際のプレイ時年齢
          }
        } else if (isPublic && userProfile.age_privacy === 1) {
          // 曖昧: 年代のみ表示（小学生・中学生は学校段階のみ）
          const schoolGrade = calculateSchoolGrade(userProfile.birth_date, dateToUse);
          if (schoolGrade && schoolGrade.includes('小学')) {
            ageAtPlay = '小学生';
          } else if (schoolGrade && schoolGrade.includes('中学')) {
            ageAtPlay = '中学生';
          } else {
            // 10歳未満は「6歳未満」として表示
            if (exactAge < 7) {
              ageAtPlay = '6歳未満';
            } else {
              const decade = Math.floor(exactAge / 10) * 10;
              const isFirstHalf = exactAge % 10 < 5;
              ageAtPlay = `${decade}代${isFirstHalf ? '前半' : '後半'}`;
            }
          }
        }
      }
      // age_privacy === 0 の場合は年齢情報を表示しない
    }

    // 日付の表示形式を簡略化
    const year = playDate.getFullYear();
    const month = playDate.getMonth() + 1;
    const currentYear = new Date().getFullYear();
    
    let dateDisplay;
    if (year === currentYear) {
      dateDisplay = `${month}月`;
    } else if (yearsAgo >= 5) {
      dateDisplay = `${year}年${yearsAgo > 1 ? ` / ${yearsAgo}年前` : ''}`;
    } else {
      dateDisplay = `${year}年${month}月${yearsAgo > 1 ? ` / ${yearsAgo}年前` : ''}`;
    }

    return {
      dateDisplay,
      ageAtPlay,
      actualPlayDate: dateToUse
    };
  };

  const playDateInfo = formatPlayDate(memory);

  const getCoverUrl = (coverImage: string | null) => {
    if (!coverImage) return '/images/no-cover.png';
    
    // 完全なURLの場合はそのまま返す
    if (coverImage.startsWith('http://') || coverImage.startsWith('https://')) {
      return coverImage;
    }
    
    // 相対パスの場合もそのまま返す
    if (coverImage.startsWith('/')) {
      return coverImage;
    }
    
    // IGDB形式の処理
    if (coverImage.startsWith('pulse_')) {
      return `https://plsdb.com/cover/${coverImage.replace('pulse_', '')}.jpg`;
    } else {
      return `https://images.igdb.com/igdb/image/upload/t_cover_big/${coverImage}.jpg`;
    }
  };

  const handleLikeClick = async () => {
    if (!localStorage.getItem('token')) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    try {
      if (userLikeCount >= 10) {
        console.error('いいねの上限（10回）に達しています。');
        return;
      }

      const response = await toggleMemoryLike(memory.id);
      setIsLiked(true);
      setLikeCount(response.data.total_likes);
      setUserLikeCount(response.data.user_like_count);
      onUpdate?.();
    } catch (error: any) {
      console.error('いいね操作に失敗しました:', error);
      if (error?.response?.data?.error) {
        console.error(error.response.data.error);
      }
    }
  };

  const shouldTruncate = memory.content.length > maxLength || memory.content.split('\n').length > maxLines;

  return (
    <Box
      sx={{
        position: 'relative',
        mb: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* メインコンテンツ */}
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {/* ゲームカバー */}
          <Box sx={{ flexShrink: 0 }}>
            <Link to={`/game/${memory.game.id}`}>
              <Box
                component="img"
                src={getCoverUrl(memory.game.cover_id)}
                alt={memory.game.name}
                sx={{
                  width: { xs: 60, md: 90 },
                  height: { xs: 80, md: 120 },
                  borderRadius: 1,
                  objectFit: 'cover',
                  cursor: 'pointer',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
                  }
                }}
              />
            </Link>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* ヘッダー情報 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {showUserInfo && (
                <Avatar
                  src={memory.is_anonymous ? undefined : memory.avatar || undefined}
                  sx={{ 
                    width: 28, 
                    height: 28,
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    fontSize: '0.8rem'
                  }}
                >
                  {memory.is_anonymous ? '?' : memory.display_name?.[0] || memory.username[0]}
                </Avatar>
              )}
              
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {showUserInfo && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.7rem',
                      lineHeight: 1.2,
                      display: 'block'
                    }}
                  >
                    {memory.is_anonymous ? '匿名ユーザー' : (
                      <MuiLink 
                        component={Link} 
                        to={`/profile/${memory.username}`}
                        sx={{
                          color: 'inherit',
                          textDecoration: 'none',
                          '&:hover': {
                            color: '#fff',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {memory.display_name || memory.username}
                      </MuiLink>
                    )}
                    {' の思い出'}
                  </Typography>
                )}
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: showUserInfo ? 0.25 : 0 }}>
                  <MuiLink 
                    component={Link} 
                    to={`/game/${memory.game.id}`}
                    sx={{
                      color: 'text.secondary',
                      textDecoration: 'none',
                      fontSize: '0.7rem',
                      fontWeight: 400,
                      lineHeight: 1.2,
                      '&:hover': {
                        color: 'primary.light',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {memory.game.name}
                  </MuiLink>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {/* 公開設定の表示を一時的にバイパス
                    {memory.is_public ? (
                      <Tooltip title="公開">
                        <Public 
                          fontSize="small" 
                          sx={{ color: 'rgba(76, 175, 80, 0.8)', fontSize: '0.8rem' }} 
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="非公開">
                        <VisibilityOff 
                          fontSize="small" 
                          sx={{ color: 'rgba(255, 193, 7, 0.8)', fontSize: '0.8rem' }} 
                        />
                      </Tooltip>
                    )}
                    */}
                    
                    {/* {memory.is_anonymous && (
                      <Chip 
                        label="匿名" 
                        size="small" 
                        sx={{ 
                          fontSize: '0.6rem', 
                          height: 18,
                          background: 'linear-gradient(45deg, rgba(156, 39, 176, 0.3), rgba(156, 39, 176, 0.5))',
                          color: '#CE93D8',
                          border: '1px solid rgba(156, 39, 176, 0.4)',
                          fontWeight: 600,
                          '& .MuiChip-label': {
                            px: 0.5
                          }
                        }}
                      />
                    )} */}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* メモリータイトル */}
            <Typography 
              component="h2"
              variant="subtitle2" 
              sx={{ 
                mb: 1, 
                fontWeight: 500,
                fontSize: { xs: '0.8rem', md: '0.85rem' },
                color: '#fff',
                lineHeight: 1.25
              }}
            >
              {memory.title}
            </Typography>

            {/* プレイ日付と関連情報 */}
            {playDateInfo && (
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                alignItems: 'center', 
                gap: { xs: 0.5, sm: 0.75 }, 
                mb: 1.5,
                '& > *': {
                  flexShrink: 0
                }
              }}>
                {/* 日付チップ */}
                <Tooltip title={`プレイ日: ${new Date(playDateInfo.actualPlayDate).toLocaleDateString('ja-JP')}`} arrow>
                  <Chip
                    icon={<AccessTime />}
                    label={playDateInfo.dateDisplay}
                    size="small"
                    sx={{
                      background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.2), rgba(33, 150, 243, 0.3))',
                      color: '#90CAF9',
                      border: '1px solid rgba(33, 150, 243, 0.4)',
                      borderRadius: '4px',
                      fontWeight: 500,
                      fontSize: '0.65rem',
                      height: 22,
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      cursor: 'default',
                      '&:hover': {
                        background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.3), rgba(33, 150, 243, 0.4))',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
                      },
                      '& .MuiChip-icon': {
                        fontSize: '0.75rem',
                        color: '#90CAF9',
                        marginLeft: '4px'
                      },
                      '& .MuiChip-label': {
                        px: 0.75,
                        zIndex: 1
                      }
                    }}
                  />
                </Tooltip>

                {/* 年齢チップ */}
                {playDateInfo.ageAtPlay !== null && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="プレイ時の年齢" arrow>
                      <Chip
                        
                        label={typeof playDateInfo.ageAtPlay === 'string' ? playDateInfo.ageAtPlay : `${playDateInfo.ageAtPlay}歳`}
                        size="small"
                        sx={{
                          background: 'linear-gradient(45deg, rgba(255, 152, 0, 0.2), rgba(255, 152, 0, 0.3))',
                          color: '#FFB74D',
                          border: '1px solid rgba(255, 152, 0, 0.4)',
                          borderRadius: '4px',
                          fontWeight: 500,
                          fontSize: '0.65rem',
                          height: 22,
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          cursor: 'default',
                          '&:hover': {
                            background: 'linear-gradient(45deg, rgba(255, 152, 0, 0.3), rgba(255, 152, 0, 0.4))',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                          },
                          '& .MuiChip-icon': {
                            fontSize: '0.75rem',
                            color: '#FFB74D',
                            marginLeft: '4px'
                          },
                          '& .MuiChip-label': {
                            px: 0.75
                          }
                        }}
                      />
                    </Tooltip>
                    
                    {/* プライバシー設定が非公開の場合のアイコン表示（自分のプロフィールページのみ） */}
                    {!isPublic && userProfile?.age_privacy === 0 && (
                      <Tooltip title="プライバシー設定により年齢は公開されません" arrow>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 22,
                            height: 22,
                            borderRadius: '4px',
                            background: 'rgba(255, 255, 255, 0.1)',
                            cursor: 'help',
                            '&:hover': {
                              background: 'linear-gradient(45deg, rgba(158, 158, 158, 0.3), rgba(158, 158, 158, 0.4))',
                              boxShadow: '0 2px 8px rgba(158, 158, 158, 0.3)',
                            }
                          }}
                        >
                          <Lock 
                            sx={{ 
                              fontSize: '0.7rem',
                              color: '#BDBDBD'
                            }} 
                          />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* メモリー内容 */}
            <Box sx={{ 
              mb: 1.5,
              mt: -0.5,
              pt: 1,
              borderTop: '1px solid rgba(255, 255, 255, 0.12)',
              position: 'relative'
            }}>
              <Box 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: { xs: '0.7rem', md: '0.75rem' },
                  lineHeight: 1.3,
                }}
              >
                {isExpanded ? formatText(memory.content) : formatText(truncateText(memory.content))}
              </Box>
              
              {shouldTruncate && !isExpanded && (
                <MuiLink
                  component="span"
                  onClick={toggleExpand}
                  sx={{
                    position: 'absolute',
                    backgroundColor: '#171717',
                    bottom: 0,
                    right: 0,
                    cursor: 'pointer',
                    color: 'primary.main',
                    fontSize: '0.7rem',
                    fontWeight: 'normal',
                    lineHeight: '1.5',
                    pl: 1,
                  }}
                >
                  全て読む
                </MuiLink>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* フッター */}
      <Box sx={{ 
        pb: 1,
        pt: 0.5,
        pl: { 
          xs: 10, // モバイル: パディング + ゲームカバー + gap
          md: 14.75  // デスクトップ: パディング + ゲームカバー + gap
        },
        pr: 2,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderTop: '1px solid rgba(255, 255, 255, 0.06)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <IconButton
            size="small"
            onClick={handleLikeClick}
            disabled={userLikeCount >= 10}
            sx={{
              color: isLiked ? '#F48FB1' : 'rgba(255, 255, 255, 0.6)',
              background: isLiked ? 'rgba(244, 143, 177, 0.15)' : 'rgba(255, 255, 255, 0.04)',
              border: '1px solid',
              borderColor: isLiked ? 'rgba(244, 143, 177, 0.3)' : 'rgba(255, 255, 255, 0.12)',
              transition: 'all 0.3s ease',
              width: 24,
              height: 24,
              '&:hover': {
                background: isLiked ? 'rgba(244, 143, 177, 0.25)' : 'rgba(255, 255, 255, 0.08)',
                transform: 'scale(1.05)',
              },
              '& .MuiSvgIcon-root': {
                fontSize: '0.8rem'
              },
              '&.Mui-disabled': {
                opacity: 0.6,
                color: '#F48FB1'
              }
            }}
          >
            {isLiked ? <ThumbUp fontSize="small" /> : <ThumbUpOutlined fontSize="small" />}
          </IconButton>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.7rem',
              fontWeight: 500
            }}
          >
            {likeCount}
            {userLikeCount > 0 && (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 'bold'
                }}
              >
                {` (+${userLikeCount})`}
              </Typography>
            )}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.5)',
              lineHeight: 1,
              fontSize: '0.6rem'
            }}
          >
            {new Date(memory.created_at).toLocaleDateString('ja-JP', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }).replace(/\//g, '/')}
          </Typography>
          
          <MuiLink
            component={Link}
            to={`/game/${memory.game.id}/memories/${memory.id}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'rgba(255, 255, 255, 0.4)',
              textDecoration: 'none',
              '&:hover': {
                color: 'primary.main'
              }
            }}
          >
            <OpenInNew sx={{ fontSize: '0.8rem' }} />
          </MuiLink>
        </Box>
      </Box>
    </Box>
  );
};

export default MemoryItem; 